import React from "react";
import { useLocation } from "react-router-dom";
import Breadcrumbs from "../components/Breadcrumbs";
import { useDeliverySettings } from "../context/DeliverySettingsContext";
import { formatUah } from "../api/siteSettingsApi";
import styles from "./CustomerServiceInfo.module.css";
import novaPoshtaLogo from "../assets/icons/nova-post-ukrainian-version.png";
import applePayLogo from "../assets/payment-systems/apple-pay.svg";
import googlePayLogo from "../assets/payment-systems/google-pay.svg";
import mastercardLogo from "../assets/payment-systems/mastercard.svg";
import visaLogo from "../assets/payment-systems/visa.svg";

import {
  LuCreditCard,
  LuPackageCheck,
  LuRefreshCw,
  LuTruck,
} from "react-icons/lu";

import {
  FaExclamationTriangle,
  FaMoneyBillWave,
  FaCreditCard,
  FaFileInvoiceDollar,
} from "react-icons/fa";

import { IoCallOutline, IoMailOutline } from "react-icons/io5";
const CUSTOMER_SERVICE_PATH = "/customer-service-info";

const onlinePaymentLogos = [
  { label: "Visa", src: visaLogo },
  { label: "Mastercard", src: mastercardLogo },
  { label: "Google Pay", src: googlePayLogo },
  { label: "Apple Pay", src: applePayLogo },
];

const paymentMethods = [
  {
    title: "Оплата при отриманні",
    description:
      "Готівкою або карткою у відділенні Нової пошти чи кур'єру Нової пошти під час адресної доставки. Комісія за переказ коштів складає: 20 грн + 2% від суми замовлення.",
    icon: FaMoneyBillWave,
  },
  {
    title: "Онлайн-оплата карткою",
    description: "Оплата банківською карткою через платіжні сервіси без комісій.",
    icon: FaCreditCard,
  },
  {
    title: "Оплата за реквізитами",
    description: "Переказ коштів на рахунок продавця за наданими реквізитами.",
    icon: FaFileInvoiceDollar,
  },
];

const anchorLinks = [
  { href: "#delivery", label: "Доставка", Icon: LuTruck },
  { href: "#payment", label: "Оплата", Icon: LuCreditCard },
  { href: "#returns", label: "Повернення та обмін", Icon: LuRefreshCw },
  { href: "#exchange", label: "Як обміняти товар?", Icon: LuPackageCheck },
];

const returnExchangeMainCards = [
  {
    title: "Обмін або повернення протягом 14 днів:",
    description:
      "ви можете обміняти або повернути товар належної якості протягом 14 днів, не рахуючи дня покупки, якщо товар не підійшов за формою, розміром, кольором або з інших причин не може бути використаний за призначенням.",
  },
  {
    title: "Товар має бути без слідів використання:",
    description:
      "обміну або поверненню підлягає тільки новий товар, який не був у використанні та не має слідів експлуатації: подряпин, сколів, потертостей, забруднень чи пошкоджень.",
  },
  {
    title: "Збережіть упаковку та комплектацію:",
    description:
      "для повернення або обміну мають бути збережені повна комплектація товару, цілісність упаковки, ярлики, маркування виробника, пломби та товарний вигляд.",
  },
];

const returnExchangeDetailCards = [
  {
    title: "Можна відмовитися від товару одразу:",
    description:
      "якщо під час огляду у відділенні Нової пошти або при адресній доставці ви виявили пошкодження, невідповідність чи іншу проблему — ви можете відмовитися від товару безпосередньо в момент отримання або повернути його кур’єру.",
  },
  {
    title: "Якщо товар має заводський брак:",
    description:
      "якщо після покупки ви виявили заводський дефект або несправність, зв’яжіться з нами. Ми допоможемо розглянути звернення та запропонуємо зручне рішення: обмін, повернення або інший варіант відповідно до законодавства.",
  },
  {
    title: "Що можна повернути або обміняти:",
    description:
      "аксесуари, амуніцію, переноски, лежанки, миски, одяг, іграшки та інші непродовольчі зоотовари — якщо вони не були у використанні та збережені в належному стані.",
  },
  {
    title: "Що не підлягає поверненню належної якості:",
    description:
      "відповідно до постанови КМУ №172, товари належної якості з окремих категорій не підлягають обміну або поверненню. Для зоотоварів це може стосуватися кормів, ласощів, ветеринарних препаратів, засобів гігієни та товарів із порушеною захисною упаковкою.",
  },
];

const returnExchangeActionCards = [
  {
    title: "Вартість зворотної доставки:",
    description:
      "якщо товар має дефект, пошкоджений під час доставки або сталася помилка з боку магазину — зворотню доставку спалчує магазин. Якщо товар належної якості просто не підійшов — зворотну доставку оплачує покупець, відповідно до тарифів Нової пошти",
  },
  {
    title: "Ми завжди шукаємо зручне рішення:",
    description:
      "ми цінуємо наших клієнтів, тому в кожній проблемній ситуації прагнемо знайти швидке, чесне та зручне рішення.",
  },
];

function CustomerServiceInfo() {
  const { settings: deliverySettings } = useDeliverySettings();
  const { branchFreeDeliveryThreshold, courierFreeDeliveryThreshold } =
    deliverySettings.novaPoshta;
  const deliveryConditions = [
    {
      label: 'у відділення "Нової пошти"',
      mobileLabel: 'у відділення "Нової пошти"',
      hint: `(безкоштовно для замовлень від ${formatUah(branchFreeDeliveryThreshold)})`,
    },
    {
      label: 'у поштомат "Нової пошти"',
      hint: `(безкоштовно для замовлень від ${formatUah(branchFreeDeliveryThreshold)})`,
      mobileOnly: true,
    },
    {
      label: 'кур\'єром "Нової пошти"',
      hint: `(безкоштовно для замовлень від ${formatUah(courierFreeDeliveryThreshold)})`,
    },
  ];
  const location = useLocation();
  const normalizedAnchorHash =
    location.hash === "#delivery-payment"
      ? "#delivery"
      : location.hash === "#returns-exchange"
        ? "#returns"
        : location.hash;

  return (
    <div className={`${styles.page} ${styles.fadeIn}`}>
      <Breadcrumbs
        paths={[
          { label: "Головна", to: "/" },
          {
            label: "Оплата, доставка, повернення та обмін",
            to: CUSTOMER_SERVICE_PATH,
          },
        ]}
      />

      <nav className={styles.anchorNav} aria-label="Розділи сторінки">
        {anchorLinks.map(({ href, label, Icon }) => {
          const isCurrent = normalizedAnchorHash === href;

          return (
            <a
              href={href}
              key={href}
              className={isCurrent ? styles.anchorNavLinkActive : undefined}
              aria-current={isCurrent ? "location" : undefined}
            >
              <Icon aria-hidden="true" />
              {label}
            </a>
          );
        })}
      </nav>

      <span
        id="delivery-payment"
        className={styles.sectionAnchor}
        aria-hidden="true"
      />

      <section
        id="delivery"
        className={`${styles.section} ${styles.deliverySection}`}
        aria-labelledby="delivery-title"
      >
        <div className={styles.sectionIntro}>
          <h2 id="delivery-title">Доставка замовлень</h2>
        </div>

        <div className={styles.deliveryServiceCard}>
          <div className={styles.deliveryServiceItem}>
            <img
              src={novaPoshtaLogo}
              alt="Нова пошта"
              className={styles.deliveryServiceIcon}
            />
            <div className={styles.deliveryServiceContent}>
              <h3 className={styles.deliveryServiceTitle}>
                Доставка Новою поштою:
              </h3>
              <span className={styles.deliveryServiceLine}>
                самовивіз з відділення, поштомату або кур'єром за вашою адресою
              </span>
              <span className={styles.deliveryConditionsTitle}>
                Вартість доставки замовлень:
              </span>
              <div
                className={styles.deliveryConditions}
                aria-label="Умови доставки Новою поштою"
              >
                {deliveryConditions.map((condition) => (
                  <div
                    className={`${styles.deliveryConditionItem} ${
                      condition.mobileOnly
                        ? styles.deliveryConditionMobileOnly
                        : ""
                    }`}
                    key={condition.label}
                  >
                    <div className={styles.deliveryConditionRow}>
                      <span className={styles.deliveryConditionLabel}>
                        {condition.mobileLabel ? (
                          <>
                            <span
                              className={styles.deliveryConditionLabelDesktop}
                            >
                              {condition.label}
                            </span>
                            <span
                              className={styles.deliveryConditionLabelMobile}
                            >
                              {condition.mobileLabel}
                            </span>
                          </>
                        ) : (
                          condition.label
                        )}
                      </span>
                      <strong className={styles.deliveryConditionValue}>
                        за тарифом перевізника
                      </strong>
                    </div>
                    <div className={styles.deliveryConditionHint}>
                      {condition.hint}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className={styles.deliveryDispatchInfo}>
            <div className={styles.deliveryInfoBlock}>
              <h3 className={styles.deliveryServiceTitle}>
                Терміни відправлення замовлень:
              </h3>
              <span className={styles.deliveryServiceLine}>
                замовлення оформлені та сплачені <strong>до 18:00</strong> (у
                випадку оплати картою он-лайн через платіжний сервіс або для
                оплат за реквізитами) відправлення здійснюється в той же день.
                Якщо <strong>після 18:00</strong> - відправлення замовлень
                наступного дня.
              </span>
            </div>

            <div className={styles.deliveryInfoBlock}>
              <h3 className={styles.deliveryServiceTitle}>
                Умови відправлення замовлень:
              </h3>
              <span className={styles.deliveryServiceLine}>
                для замовлень з обраним типом оплати: "Он-лайн оплата
                банківською карткою через платіжний сервіс" або "Оплата за
                реквізитами" відправлення замовлень здійснюється{" "}
                <strong>
                  після попереднього находження 100% оплати на рахунок продавця
                </strong>
                .
              </span>
              <span className={styles.deliveryServiceLine}>
                для замовлень з обраним типом оплати: "Оплата при отримані у
                відділенні Нової пошти" відправлення замовлень здійснюється без
                попередної предоплати.
              </span>
            </div>

            <div className={styles.deliveryInfoBlock}>
              <h3 className={styles.deliveryServiceTitle}>Важливо!</h3>
              <span className={styles.deliveryServiceLine}>
                У випадку оплати замовлень при отриманні, клієнт самостійно
                сплачує комісію за переказ коштів, відповідно до тарифів
                перевізника - оператора послуг{" "}
                <strong>
                  (фіксована ставка 20 грн + 2% від суми замовлення)
                </strong>
                .
              </span>
            </div>
          </div>
        </div>
      </section>

      <section
        id="payment"
        className={styles.section}
        aria-labelledby="payment-title"
      >
        <div className={styles.sectionIntro}>
          <h2 id="payment-title">Оплата замовлень</h2>
        </div>
        <div className={styles.paymentPanel}>
          <div className={styles.paymentMethodsList}>
            {paymentMethods.map(({ title, description, icon: Icon }) => (
              <article className={styles.paymentMethodItem} key={title}>
                <span className={styles.paymentMethodIcon}>
                  <Icon className={styles.paymentMethodSvg} />
                </span>
                <div className={styles.paymentMethodText}>
                  <h3>{title}</h3>
                  <p>{description}</p>
                </div>
              </article>
            ))}
          </div>

          <div className={styles.paymentOnlineNote}>
            <p>
              Он-лайн оплата карткою доступна через платіжні системи та сервіси:
            </p>
          </div>

          <div className={styles.paymentLogoRow} aria-label="Платіжні системи">
            {onlinePaymentLogos.map(({ label, src }) => (
              <span key={label} className={styles.paymentLogoItem}>
                <img
                  src={src}
                  alt={label}
                  className={styles.paymentLogoImage}
                  loading="lazy"
                  decoding="async"
                />
              </span>
            ))}
          </div>
        </div>
      </section>

      <span
        id="returns-exchange"
        className={styles.sectionAnchor}
        aria-hidden="true"
      />

      <section
        id="returns"
        className={styles.section}
        aria-labelledby="returns-title"
      >
        <div className={styles.sectionIntro}>
          <h2 id="returns-title">Повернення та обмін</h2>
        </div>

        <div className={styles.returnExchangeGrid}>
          {returnExchangeMainCards.map(({ title, description }) => (
            <article className={styles.returnExchangeCard} key={title}>
              <h3>{title}</h3>
              <p>{description}</p>
            </article>
          ))}
        </div>

        <article className={styles.warningCard}>
          <span className={styles.warningIcon}>
            <FaExclamationTriangle aria-hidden="true" />
          </span>
          <div className={styles.warningContent}>
            <h3>Важливо: оглядайте замовлення при отриманні</h3>
            <p>
              Радимо <strong>ОБОВ’ЯЗКОВО</strong> оглядати замовлення у
              відділенні Нової пошти або в присутності кур’єра Нової пошти. Ми
              страхуємо кожну посилку на суму її вартості, тому перевірка під
              час отримання допоможе швидко вирішити питання, якщо товар
              пошкоджено під час доставки.
            </p>
          </div>
        </article>

        <div className={styles.returnExchangeGrid}>
          {returnExchangeDetailCards.map(({ title, description }) => (
            <article className={styles.returnExchangeCard} key={title}>
              <h3>{title}</h3>
              <p>{description}</p>
            </article>
          ))}
        </div>
      </section>

      <section
        id="exchange"
        className={styles.section}
        aria-labelledby="exchange-title"
      >
        <div className={styles.sectionIntro}>
          <h2 id="exchange-title">Як обміняти або повернути товар:</h2>
        </div>

        <div className={styles.returnExchangeActionGrid}>
          <article className={styles.returnExchangeCard}>
            <h3>Як швидко оформити повернення або обмін:</h3>
            <p>
              Повідомте нам номер замовлення, причину звернення та додайте фото
              товару й упаковки. Ми перевіримо інформацію та надішлемо вам
              реквізити для повернення або обміну.
            </p>
          </article>

          <article
            className={`${styles.returnExchangeCard} ${styles.contactCard}`}
          >
            <h3>Контакти для обміну або повернення</h3>
            <p>
              Для вирішення питання щодо обміну або повернення товару
              зателефонуйте нам за номером або напишіть звернення на електронну
              адресу.
            </p>
            <div className={styles.contactLinks}>
              <a href="tel:+380671234567">
                <IoCallOutline aria-hidden="true" />
                +38 (067) 123-45-67
              </a>
              <a href="mailto:info@myzooshop.com.ua">
                <IoMailOutline aria-hidden="true" />
                info@myzooshop.com.ua
              </a>
            </div>
          </article>

          {returnExchangeActionCards.map(({ title, description }) => (
            <article className={styles.returnExchangeCard} key={title}>
              <h3>{title}</h3>
              <p>{description}</p>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}

export default CustomerServiceInfo;
